import { useEffect, useRef, useState, type RefObject } from 'react';
import { BOOT, buildBootParticles, drawBoot, sampleWord, type BootParticle } from '../utils/bootField';
import { hexToRgb } from '../utils/color';

export type BootPhase = 'word' | 'morph' | 'ready' | 'done';

/** Drives the "anna → orb" boot sequence on a canvas; reports the phase for the overlay copy. */
export function useBootAnimation(canvasRef: RefObject<HTMLCanvasElement>, accent: string, reduced: boolean) {
  const [phase, setPhase] = useState<BootPhase>(reduced ? 'ready' : 'word');
  const accentRef = useRef(accent);
  accentRef.current = accent;

  useEffect(() => {
    if (reduced) {
      const id = window.setTimeout(() => setPhase('done'), 1600);
      return () => window.clearTimeout(id);
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    let raf = 0;
    let particles: BootParticle[] = [];
    let w = 0;
    let h = 0;
    let start = 0;
    let lastPhase: BootPhase = 'word';
    let cancelled = false;

    const layout = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const begin = () => {
      if (cancelled) return;
      layout();
      const fontPx = Math.min(w * 0.24, h * 0.32, 300);
      particles = buildBootParticles(sampleWord('anna', w, h, fontPx, w / 2, h * 0.46));
      start = performance.now();
      raf = requestAnimationFrame(loop);
    };

    const loop = (now: number) => {
      const t = (now - start) / 1000;
      drawBoot({ ctx, w, h, t, cx: w / 2, cy: h * 0.46, R: Math.min(w, h) * 0.17, accent: hexToRgb(accentRef.current), particles });
      const next: BootPhase = t < BOOT.morphStart ? 'word' : t < BOOT.settled ? 'morph' : t < BOOT.done ? 'ready' : 'done';
      if (next !== lastPhase) {
        lastPhase = next;
        setPhase(next);
      }
      if (next !== 'done') raf = requestAnimationFrame(loop);
    };

    // Wait for the display face so the word is sampled in Outfit, not a fallback.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.load) fonts.load('200 120px Outfit').then(begin, begin);
    else begin();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [canvasRef, reduced]);

  return { phase, skip: () => setPhase('done') };
}
